import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useEstimate } from "../store/EstimateContext";
import { SNS_PRESETS, findSnsPreset, countSnsLevels } from "../lib/s1000dData";
import { fmtHours } from "../lib/calculations";
import { Layers, ChevronRight, ChevronDown, ListChecks } from "lucide-react";

import HelpIconButton from "./HelpIconButton";
function SnsTree({ preset, expandedSystems, toggle }) {
  return (
    <div className="font-mono text-xs space-y-1" data-testid="sns-tree">
      {preset.systems.map((s) => {
        const open = expandedSystems[s.code];
        return (
          <div key={s.code}>
            <button
              onClick={() => toggle(s.code)}
              data-testid={`sns-system-${s.code}`}
              className="inline-flex items-center gap-1 text-black font-bold hover:text-[#002FA7]"
            >
              {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              {s.code} · {s.name}
              <span className="text-gray-500 font-normal ml-2">
                ({s.subsystems.length} sub, {s.subsystems.reduce((n, ss) => n + ss.subsubsystems.length, 0)} sub-sub)
              </span>
            </button>
            {open && (
              <ul className="ml-5 mt-1 space-y-0.5">
                {s.subsystems.map((ss) => (
                  <li key={ss.code} className="text-gray-700">
                    <span className="text-gray-500">└ {ss.code}</span> · {ss.name}
                    {ss.subsubsystems.length > 0 && (
                      <ul className="ml-5">
                        {ss.subsubsystems.map((sss) => (
                          <li key={sss.code} className="text-gray-500">
                            └ {sss.code} · {sss.name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function DmlTab() {
  const { t } = useTranslation();
  const { estimate, totals, dml, updateDmlPlan, applySnsPreset, formatCurrency } = useEstimate();
  const plan = estimate.dmlPlan;
  const preset = findSnsPreset(plan.snsPresetId);
  const counts = countSnsLevels(preset);
  const [expanded, setExpanded] = useState({});
  const toggle = (code) => setExpanded((e) => ({ ...e, [code]: !e[code] }));

  return (
    <section className="space-y-6" data-testid="dml-tab">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
            {t("dml.section")}
          </p>
          <h2 className="font-heading text-2xl tracking-tight font-bold text-black flex items-center gap-2">
            {t("dml.title")}
            <HelpIconButton anchor="ch2" testId="help-icon-dml" />
          </h2>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            {t("dml.description")}
          </p>
        </div>
        <div className="border border-gray-300 px-4 py-3 bg-white min-w-[220px]" data-testid="dml-grand-card">
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
            DML Grand Total
          </p>
          <p className="font-mono text-2xl font-bold text-[#002FA7] leading-none mt-1">
            {formatCurrency(dml.grandTotal)}
          </p>
          <p className="text-[10px] uppercase tracking-wider text-gray-500 mt-1">
            {fmtHours(dml.totalHours)} hrs · {dml.dmcCount + dml.icnCount + dml.pmcCount} codes
          </p>
        </div>
      </div>

      {/* SNS PICKER */}
      <div className="bg-white border border-gray-300 p-5" data-testid="dml-sns-picker">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Layers size={16} className="text-[#002FA7]" />
              <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
                Standard Numbering System
              </p>
            </div>
            <h3 className="font-heading text-lg tracking-tight font-bold text-black">
              {preset.name}
            </h3>
            <p className="text-sm text-gray-600 mt-1">{preset.description}</p>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500 block">
                SNS Preset
              </label>
              <select
                data-testid="dml-sns-select"
                value={plan.snsPresetId}
                onChange={(e) => applySnsPreset(e.target.value)}
                className="border border-gray-300 rounded-none px-2 py-1.5 mt-1 font-mono text-sm bg-white"
              >
                {SNS_PRESETS.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
          <div className="border border-gray-300 p-3" data-testid="dml-sns-counts">
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
              Preset Hierarchy Totals
            </p>
            <table className="w-full text-sm mt-2 font-mono">
              <tbody>
                <tr><td className="text-gray-500">Systems</td><td className="text-right font-bold">{counts.systems}</td></tr>
                <tr><td className="text-gray-500">Sub-systems</td><td className="text-right font-bold">{counts.subsystems}</td></tr>
                <tr><td className="text-gray-500">Sub-subsystems</td><td className="text-right font-bold">{counts.subsubsystems}</td></tr>
                <tr className="border-t border-gray-200"><td className="text-gray-500 pt-1">DM matrix totals</td><td className="text-right text-[#002FA7] pt-1">
                  {totals.totalDms} DMs · {totals.totalIllus} illus
                </td></tr>
              </tbody>
            </table>
          </div>
          <div className="border border-gray-300 p-3 max-h-72 overflow-y-auto" data-testid="dml-sns-tree-container">
            <SnsTree preset={preset} expandedSystems={expanded} toggle={toggle} />
          </div>
        </div>
      </div>

      {/* DML INPUTS */}
      <div className="bg-white border border-gray-300 p-5" data-testid="dml-inputs">
        <div className="flex items-center gap-2 mb-4">
          <ListChecks size={16} className="text-[#002FA7]" />
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
            Codes &amp; Minutes
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border border-gray-300 p-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">Data Module Code (DMC)</p>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div>
                <label className="text-[10px] uppercase text-gray-500 block">Count (0 = auto)</label>
                <input type="number" min="0" value={plan.dmcCount}
                  onChange={(e) => updateDmlPlan("dmcCount", Number(e.target.value) || 0)}
                  data-testid="dml-dmc-count"
                  className="border border-gray-300 rounded-none px-2 py-1 mt-1 font-mono text-sm w-full text-right" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-gray-500 block">Min / DMC</label>
                <input type="number" min="0" step="0.5" value={plan.minutesPerDmc}
                  onChange={(e) => updateDmlPlan("minutesPerDmc", Number(e.target.value) || 0)}
                  data-testid="dml-dmc-min"
                  className="border border-gray-300 rounded-none px-2 py-1 mt-1 font-mono text-sm w-full text-right" />
              </div>
            </div>
            <p className="text-[10px] text-gray-500 mt-2 font-mono" data-testid="dml-dmc-resolved">
              {dml.dmcCount} codes × {dml.minutesPerDmc} min = {fmtHours(dml.dmcMinutes / 60)} hrs
            </p>
          </div>
          <div className="border border-gray-300 p-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">Information Control No. (ICN)</p>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div>
                <label className="text-[10px] uppercase text-gray-500 block">Count (0 = auto)</label>
                <input type="number" min="0" value={plan.icnCount}
                  onChange={(e) => updateDmlPlan("icnCount", Number(e.target.value) || 0)}
                  data-testid="dml-icn-count"
                  className="border border-gray-300 rounded-none px-2 py-1 mt-1 font-mono text-sm w-full text-right" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-gray-500 block">Min / ICN</label>
                <input type="number" min="0" step="0.5" value={plan.minutesPerIcn}
                  onChange={(e) => updateDmlPlan("minutesPerIcn", Number(e.target.value) || 0)}
                  data-testid="dml-icn-min"
                  className="border border-gray-300 rounded-none px-2 py-1 mt-1 font-mono text-sm w-full text-right" />
              </div>
            </div>
            <p className="text-[10px] text-gray-500 mt-2 font-mono" data-testid="dml-icn-resolved">
              {dml.icnCount} codes × {dml.minutesPerIcn} min = {fmtHours(dml.icnMinutes / 60)} hrs
            </p>
          </div>
          <div className="border border-gray-300 p-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">Publication Module Code (PMC)</p>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div>
                <label className="text-[10px] uppercase text-gray-500 block">Count</label>
                <input type="number" min="0" value={plan.pmcCount}
                  onChange={(e) => updateDmlPlan("pmcCount", Number(e.target.value) || 0)}
                  data-testid="dml-pmc-count"
                  className="border border-gray-300 rounded-none px-2 py-1 mt-1 font-mono text-sm w-full text-right" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-gray-500 block">Min / PMC</label>
                <input type="number" min="0" step="0.5" value={plan.minutesPerPmc}
                  onChange={(e) => updateDmlPlan("minutesPerPmc", Number(e.target.value) || 0)}
                  data-testid="dml-pmc-min"
                  className="border border-gray-300 rounded-none px-2 py-1 mt-1 font-mono text-sm w-full text-right" />
              </div>
            </div>
            <p className="text-[10px] text-gray-500 mt-2 font-mono" data-testid="dml-pmc-resolved">
              {dml.pmcCount} codes × {dml.minutesPerPmc} min = {fmtHours(dml.pmcMinutes / 60)} hrs
            </p>
          </div>
        </div>
      </div>

      {/* DML COST SUMMARY */}
      <div className="bg-white border border-gray-300" data-testid="dml-summary">
        <div className="border-b border-gray-300 p-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">Cost Summary</p>
          <h3 className="font-heading text-lg tracking-tight font-bold text-black">DML Effort &amp; Cost</h3>
          <p className="text-xs text-gray-500 mt-1 font-mono">
            Costed at the project blended rate {formatCurrency(dml.blendedRate)} / hr
            (overhead {estimate.overheadPercent || 0}% · contingency {estimate.contingencyPercent || 0}%).
          </p>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-100 text-black font-bold uppercase tracking-wider text-[10px] border-b border-gray-300">
              <th className="py-2 px-3 text-left">Artefact</th>
              <th className="py-2 px-3 text-right">Count</th>
              <th className="py-2 px-3 text-right">Min / unit</th>
              <th className="py-2 px-3 text-right">Hours</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-200" data-testid="dml-row-dmc">
              <td className="py-2 px-3 font-semibold">Data Module Codes</td>
              <td className="py-2 px-3 font-mono text-right">{dml.dmcCount}</td>
              <td className="py-2 px-3 font-mono text-right">{dml.minutesPerDmc}</td>
              <td className="py-2 px-3 font-mono text-right">{fmtHours(dml.dmcMinutes / 60)}</td>
            </tr>
            <tr className="border-b border-gray-200" data-testid="dml-row-icn">
              <td className="py-2 px-3 font-semibold">Information Control Numbers</td>
              <td className="py-2 px-3 font-mono text-right">{dml.icnCount}</td>
              <td className="py-2 px-3 font-mono text-right">{dml.minutesPerIcn}</td>
              <td className="py-2 px-3 font-mono text-right">{fmtHours(dml.icnMinutes / 60)}</td>
            </tr>
            <tr className="border-b border-gray-200" data-testid="dml-row-pmc">
              <td className="py-2 px-3 font-semibold">Publication Module Codes</td>
              <td className="py-2 px-3 font-mono text-right">{dml.pmcCount}</td>
              <td className="py-2 px-3 font-mono text-right">{dml.minutesPerPmc}</td>
              <td className="py-2 px-3 font-mono text-right">{fmtHours(dml.pmcMinutes / 60)}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr className="bg-black text-white font-bold uppercase tracking-wider text-[10px]">
              <td className="py-3 px-3" colSpan={3}>Totals</td>
              <td className="py-3 px-3 font-mono text-right normal-case" data-testid="dml-total-hours">{fmtHours(dml.totalHours)}</td>
            </tr>
          </tfoot>
        </table>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 p-4 border-t border-gray-300 bg-gray-50">
          <div className="bg-white border border-gray-300 p-3" data-testid="dml-stat-base">
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">Base Cost</p>
            <p className="font-mono text-xl font-bold mt-1">{formatCurrency(dml.baseCost)}</p>
          </div>
          <div className="bg-white border border-gray-300 p-3" data-testid="dml-stat-overhead">
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">Overhead</p>
            <p className="font-mono text-xl font-bold mt-1">{formatCurrency(dml.overheadCost)}</p>
          </div>
          <div className="bg-white border border-gray-300 p-3" data-testid="dml-stat-contingency">
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">Contingency</p>
            <p className="font-mono text-xl font-bold mt-1">{formatCurrency(dml.contingencyCost)}</p>
          </div>
          <div className="bg-white border border-gray-300 p-3" data-testid="dml-stat-grand">
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">DML Grand Total</p>
            <p className="font-mono text-xl font-black text-[#002FA7] mt-1">{formatCurrency(dml.grandTotal)}</p>
          </div>
        </div>
      </div>

    </section>
  );
}
